import * as THREE from 'three';
import { Sky } from 'three/examples/jsm/objects/Sky.js';
import { Lensflare, LensflareElement } from 'three/examples/jsm/objects/Lensflare.js';
import { assetUrl } from './assetUrl';

export type AtmosphereHandles = {
  sun: THREE.DirectionalLight;
  hemi: THREE.HemisphereLight;
  sky: Sky;
  sunDir: THREE.Vector3;
  lensflare: Lensflare;
  update: (camera: THREE.Camera) => void;
};

/**
 * ICO-warm late afternoon sky: Preetham atmosphere + sun disk,
 * exp fog matched to horizon, soft additive god rays, and occluding lens flares.
 */
export function setupAtmosphere(
  scene: THREE.Scene,
  renderer: THREE.WebGLRenderer,
): AtmosphereHandles {
  const sunDir = new THREE.Vector3(-0.55, 0.72, 0.35).normalize();

  const sky = new Sky();
  sky.scale.setScalar(450);
  scene.add(sky);

  const uniforms = sky.material.uniforms;
  uniforms['turbidity'].value = 4.5;
  uniforms['rayleigh'].value = 2.2;
  uniforms['mieCoefficient'].value = 0.006;
  uniforms['mieDirectionalG'].value = 0.85;
  uniforms['sunPosition'].value.copy(sunDir).multiplyScalar(400);

  // Horizon-matched fog (warm stone haze) — slightly denser so cliffs fall off into mist
  const fogColor = new THREE.Color(0xb89678);
  scene.background = null;
  scene.fog = new THREE.FogExp2(fogColor.getHex(), 0.014);

  const hemi = new THREE.HemisphereLight(0xffe2c4, 0x4a4038, 0.55);
  scene.add(hemi);

  const sun = new THREE.DirectionalLight(0xffd4a8, 2.6);
  sun.position.copy(sunDir).multiplyScalar(40);
  sun.castShadow = true;
  sun.shadow.mapSize.set(2048, 2048);
  sun.shadow.camera.near = 2;
  sun.shadow.camera.far = 80;
  sun.shadow.camera.left = -32;
  sun.shadow.camera.right = 32;
  sun.shadow.camera.top = 32;
  sun.shadow.camera.bottom = -32;
  sun.shadow.bias = -0.00025;
  sun.shadow.normalBias = 0.035;
  scene.add(sun);
  scene.add(sun.target);
  sun.target.position.set(0, 2, 2);

  const fill = new THREE.DirectionalLight(0x7a8aaa, 0.32);
  fill.position.set(14, 8, -18);
  scene.add(fill);

  const rim = new THREE.PointLight(0xffb070, 1.05, 42, 2);
  rim.position.set(-8, 8, 6);
  scene.add(rim);

  const bounce = new THREE.PointLight(0xc9a06a, 0.5, 28, 2);
  bounce.position.set(0, 1.5, 2);
  scene.add(bounce);

  // Soft sun glow disc (extra bloom fuel when in view)
  const sunGlow = new THREE.Mesh(
    new THREE.SphereGeometry(2.2, 16, 12),
    new THREE.MeshBasicMaterial({
      color: 0xffe6c0,
      transparent: true,
      opacity: 0.55,
      depthWrite: false,
      fog: false,
      toneMapped: false,
    }),
  );
  sunGlow.position.copy(sunDir).multiplyScalar(180);
  sunGlow.renderOrder = -1;
  scene.add(sunGlow);

  const textureLoader = new THREE.TextureLoader();

  // Soft ethereal god rays — gradient alpha shafts (not bare glass panes)
  const shaftMap = textureLoader.load(assetUrl('textures/godray/shaft.png'));
  shaftMap.colorSpace = THREE.SRGBColorSpace;
  shaftMap.wrapS = THREE.ClampToEdgeWrapping;
  shaftMap.wrapT = THREE.ClampToEdgeWrapping;
  shaftMap.generateMipmaps = true;
  shaftMap.minFilter = THREE.LinearMipmapLinearFilter;
  shaftMap.magFilter = THREE.LinearFilter;

  const shaftMat = new THREE.MeshBasicMaterial({
    map: shaftMap,
    color: 0xffd4a0,
    transparent: true,
    opacity: 0.28,
    depthWrite: false,
    depthTest: true,
    side: THREE.DoubleSide,
    // SrcAlpha + One: soft alpha attenuates additive light (not glass panes)
    blending: THREE.CustomBlending,
    blendEquation: THREE.AddEquation,
    blendSrc: THREE.SrcAlphaFactor,
    blendDst: THREE.OneFactor,
    blendSrcAlpha: THREE.ZeroFactor,
    blendDstAlpha: THREE.OneFactor,
    fog: false,
    toneMapped: false,
  });

  const up = new THREE.Vector3(0, 1, 0);
  const shafts = new THREE.Group();
  shafts.renderOrder = 2;

  // Sparse courtyard placements — few beams, AAA density not clutter
  const shaftSpecs: { x: number; z: number; width: number; length: number; opacity: number }[] = [
    { x: -5.2, z: -1.5, width: 2.4, length: 22, opacity: 0.32 },
    { x: -1.4, z: 2.8, width: 3.1, length: 24, opacity: 0.4 },
    { x: 2.6, z: 0.4, width: 2.2, length: 20, opacity: 0.28 },
    { x: 5.5, z: 4.2, width: 2.8, length: 23, opacity: 0.34 },
  ];

  const shaftBases: THREE.Vector3[] = [];
  const shaftLengths: number[] = [];

  for (let i = 0; i < shaftSpecs.length; i++) {
    const spec = shaftSpecs[i];
    const beam = new THREE.Group();
    // Crossed soft planes → volume without hard polygon silhouettes
    const geo = new THREE.PlaneGeometry(spec.width, spec.length, 1, 1);
    const a = new THREE.Mesh(geo, shaftMat.clone());
    a.material.opacity = spec.opacity;
    a.renderOrder = 2;
    const b = new THREE.Mesh(geo, shaftMat.clone());
    b.material.opacity = spec.opacity * 0.85;
    b.rotation.y = Math.PI * 0.5;
    b.renderOrder = 2;
    beam.add(a, b);

    // Align local +Y with sun direction (shaft length toward sky)
    beam.quaternion.setFromUnitVectors(up, sunDir);
    beam.userData.baseQuat = beam.quaternion.clone();

    // Anchor near courtyard floor, center of plane along the ray
    const base = new THREE.Vector3(spec.x, 0.4, spec.z);
    shaftBases.push(base.clone());
    shaftLengths.push(spec.length);
    beam.position.copy(base).addScaledVector(sunDir, spec.length * 0.42);
    beam.userData.driftPhase = i * 1.7;
    beam.userData.driftAmp = 0.012 + (i % 2) * 0.006;
    shafts.add(beam);
  }
  scene.add(shafts);

  const flare0 = textureLoader.load(assetUrl('textures/lensflare/lensflare0.png'));
  const flare1 = textureLoader.load(assetUrl('textures/lensflare/lensflare1.png'));
  const flare2 = textureLoader.load(assetUrl('textures/lensflare/lensflare2.png'));
  const flare3 = textureLoader.load(assetUrl('textures/lensflare/lensflare3.png'));

  const lensflare = new Lensflare();
  lensflare.addElement(new LensflareElement(flare0, 720, 0, new THREE.Color(0xffe0b8)));
  lensflare.addElement(new LensflareElement(flare2, 80, 0.12, new THREE.Color(0xffc090)));
  lensflare.addElement(new LensflareElement(flare3, 140, 0.28, new THREE.Color(0xffb878)));
  lensflare.addElement(new LensflareElement(flare1, 90, 0.42, new THREE.Color(0xffd0a0)));
  lensflare.addElement(new LensflareElement(flare2, 160, 0.58, new THREE.Color(0xffcc88)));
  lensflare.addElement(new LensflareElement(flare3, 220, 0.72, new THREE.Color(0xffaa66)));
  lensflare.addElement(new LensflareElement(flare1, 70, 0.88, new THREE.Color(0xffe8c8)));
  lensflare.position.copy(sunDir).multiplyScalar(200);
  scene.add(lensflare);

  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.08;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;

  const tmp = new THREE.Vector3();
  const driftAxis = new THREE.Vector3();
  const driftQuat = new THREE.Quaternion();
  const update = (_camera: THREE.Camera) => {
    // Keep flare / glow locked to sun direction (infinite distance feel)
    tmp.copy(sunDir).multiplyScalar(200);
    lensflare.position.copy(tmp);
    sunGlow.position.copy(sunDir).multiplyScalar(180);

    // Subtle ethereal drift — slow sway perpendicular to sun, keep alignment
    const t = performance.now() * 0.001;
    driftAxis.set(-sunDir.z, 0, sunDir.x).normalize();
    for (let i = 0; i < shafts.children.length; i++) {
      const beam = shafts.children[i];
      const phase = beam.userData.driftPhase as number;
      const amp = beam.userData.driftAmp as number;
      const sway = Math.sin(t * 0.18 + phase) * amp;
      const lift = Math.sin(t * 0.11 + phase * 0.7) * amp * 0.35;
      beam.position
        .copy(shaftBases[i])
        .addScaledVector(sunDir, shaftLengths[i] * 0.42)
        .addScaledVector(driftAxis, sway * 8)
        .addScaledVector(up, lift * 4);
      driftQuat.setFromAxisAngle(sunDir, Math.sin(t * 0.14 + phase) * amp);
      beam.quaternion.copy(beam.userData.baseQuat as THREE.Quaternion).multiply(driftQuat);
    }
  };

  return { sun, hemi, sky, sunDir, lensflare, update };
}
